import React, { useState } from "react";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import LingentFloatingIcon from "@/components/LingentFloatingIcon";
import ComposerPanel from "@/components/ComposerPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Loader2, ExternalLink, Wand2, BookText, Newspaper, Globe } from "lucide-react";

const CATEGORIES = ["Computer Vision", "LLMs", "VoiceBots", "General AI"];

const SOURCE_META = {
  arxiv: { label: "arXiv", icon: BookText, color: "text-emerald-400" },
  tavily: { label: "Web", icon: Globe, color: "text-klein" },
  newsapi: { label: "News", icon: Newspaper, color: "text-safety" },
};

export default function ResearchPage() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Computer Vision");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [draftLoading, setDraftLoading] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [seedArticle, setSeedArticle] = useState(null);

  const search = async () => {
    setLoading(true);
    setResults([]);
    try {
      const { data } = await api.post("/research/discover", {
        query,
        category,
        sources: ["arxiv", "tavily", "newsapi"],
      });
      setResults(data.results || []);
      setCounts(data.counts || {});
      toast.success(
        `Found ${data.results.length} results · arXiv ${data.counts.arxiv}, Web ${data.counts.tavily}, News ${data.counts.newsapi}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed");
    } finally {
      setLoading(false);
    }
  };

  const draft = async (item) => {
    setDraftLoading(item.url);
    try {
      const { data } = await api.post("/research/draft-from-source", {
        url: item.url,
        title: item.title,
        snippet: item.snippet,
        category,
      });
      setSeedArticle({
        topic: item.title,
        notes: `Inspired by ${item.url}`,
        category,
        title: data.title,
        body: data.body,
        hashtags: data.hashtags,
        cover_image: null,
        status: "draft",
        id: null,
      });
      setComposerOpen(true);
      toast.success("Drafted from source");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally {
      setDraftLoading(null);
    }
  };

  const filtered = filter === "all" ? results : results.filter((r) => r.source === filter);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminNav />
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <div className="label-mono text-safety">// research</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-2">
          What's hot in AI today?
        </h1>
        <p className="mt-3 text-bone/60 max-w-2xl">
          Pulls fresh material from <span className="text-bone">arXiv</span> (research papers),
          <span className="text-bone"> Tavily</span> (web search), and <span className="text-bone">NewsAPI</span> (news).
          Tap any item to draft a LinkedIn post inspired by it.
        </p>

        {/* Search bar */}
        <div className="mt-8 grid lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-7">
            <Label className="label-mono text-bone/70">Search topic / keywords</Label>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. defect detection cement plant; or leave blank for category-only"
              onKeyDown={(e) => e.key === "Enter" && search()}
              className="mt-2 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-11"
              data-testid="research-query-input"
            />
          </div>
          <div className="lg:col-span-3">
            <Label className="label-mono text-bone/70">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-2 rounded-none bg-surface border-hairline h-11" data-testid="research-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-surface border-hairline">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <Button
              onClick={search}
              disabled={loading}
              data-testid="research-search-btn"
              className="w-full rounded-none bg-klein hover:bg-klein-hover text-white label-mono h-11"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Discover
            </Button>
          </div>
        </div>

        {/* Source filter chips */}
        {results.length > 0 && (
          <div className="mt-8 flex items-center gap-2 flex-wrap border-b border-hairline pb-3">
            {["all", "arxiv", "tavily", "newsapi"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`research-filter-${s}`}
                className={`label-mono px-3 py-1.5 border ${
                  filter === s
                    ? "bg-bone text-ink border-bone"
                    : "border-hairline text-bone/70 hover:text-bone"
                }`}
              >
                {s === "all"
                  ? `all (${results.length})`
                  : `${SOURCE_META[s]?.label} (${counts[s] || 0})`}
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="research-results">
          {filtered.length === 0 && !loading && (
            <div className="md:col-span-2 xl:col-span-3 border border-hairline p-12 text-center text-bone/50">
              No results yet. Enter a topic and click <span className="text-bone">Discover</span>.
            </div>
          )}
          {filtered.map((item, idx) => {
            const meta = SOURCE_META[item.source] || SOURCE_META.arxiv;
            const Icon = meta.icon;
            return (
              <div key={`${item.url}-${idx}`} className="border border-hairline bg-surface hover:bg-surface-2 transition p-5 flex flex-col" data-testid={`research-card-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`flex items-center gap-1.5 label-mono ${meta.color}`}>
                    <Icon className="w-3 h-3" />
                    {meta.label}
                  </div>
                  {item.published_at && (
                    <span className="label-mono text-[10px] text-bone/40">
                      {String(item.published_at).slice(0, 10)}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-lg font-bold tracking-tightest leading-tight line-clamp-3">
                  {item.title}
                </h3>
                <p className="mt-2 text-bone/65 text-sm line-clamp-4 leading-relaxed flex-1">
                  {item.snippet}
                </p>
                <div className="mt-4 pt-3 border-t border-hairline flex gap-2">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="label-mono text-bone/60 hover:text-bone flex items-center gap-1 text-xs"
                    data-testid={`research-open-${idx}`}
                  >
                    <ExternalLink className="w-3 h-3" /> source
                  </a>
                  <Button
                    size="sm"
                    onClick={() => draft(item)}
                    disabled={draftLoading === item.url}
                    data-testid={`research-draft-${idx}`}
                    className="ml-auto rounded-none bg-safety hover:bg-safety/90 text-white label-mono h-8"
                  >
                    {draftLoading === item.url ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                    Draft post
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <LingentFloatingIcon onOpen={() => { setSeedArticle(null); setComposerOpen(true); }} />
      <ComposerPanel
        open={composerOpen}
        onClose={() => { setComposerOpen(false); setSeedArticle(null); }}
        onSaved={() => { setComposerOpen(false); setSeedArticle(null); toast.success("Saved to articles"); }}
        article={seedArticle}
      />
    </div>
  );
}
